/*
 * Mini-cart Enova (snippets/cart-drawer.liquid).
 * - Apertura: refleja el estado de cart.js en .is-open (curvas distintas para abrir y cerrar)
 *   y arranca la transición sin esperar; precarga las imágenes del carrito. Con ?cart=open en
 *   la URL (así redirige /cart) se abre al cargar.
 * - Agregar al carrito: abre el drawer en el mismo clic (sin esperar la respuesta de
 *   Shopify) con un indicador de carga; si falla, lo cierra para que se vea el error.
 * - Pestañas «Carrito» / «Vistos recientemente».
 * - Vistos recientemente: lista por Section Rendering API, agregar por talla e indicador de
 *   unidades en el carrito.
 * La hoja de pago vive en enova-minicart-checkout.js.
 */
(() => {
  const RECENT_STORAGE_KEY = 'minimog-recently-viewed';
  const RECENT_LIMIT = 12;

  function getRecentIds() {
    try {
      const ids = JSON.parse(window.localStorage.getItem(RECENT_STORAGE_KEY) || '[]');
      return Array.isArray(ids) ? ids : [];
    } catch (error) {
      return [];
    }
  }

  function getRoutes() {
    return (window.MinimogSettings && window.MinimogSettings.routes) || {};
  }

  function emitCartUpdate(response) {
    const events = window.MinimogEvents;
    const pubSub = window.MinimogTheme && window.MinimogTheme.pubSubEvents;
    if (events && typeof events.emit === 'function' && pubSub) {
      events.emit(pubSub.cartUpdate, { cart: response, productVariantId: response && response.variant_id });
    } else {
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
    }
  }

  function showError(message) {
    const notification = window.MinimogTheme && window.MinimogTheme.Notification;
    if (!notification) return;
    notification.show({
      target: document.body,
      method: 'appendChild',
      type: 'error',
      message,
      last: 3000,
      sticky: true,
    });
  }

  // Igual que el formulario de producto del tema: pide las secciones que el carrito
  // necesita para volver a pintarse.
  function addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    formData.append('sections', sections.join(','));
    formData.append('sections_url', window.location.pathname);

    return fetch(getRoutes().cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    })
      .then((response) => response.json())
      .then((response) => {
        if (response && response.status) {
          throw new Error(response.message || response.description || 'Cart add error');
        }
        return response;
      });
  }

  class EnovaMinicart {
    constructor(root) {
      this.root = root;
      this.recent = root.querySelector('[data-enova-minicart-recent]');
      this.msg = (this.recent && this.recent.dataset) || {};
      this.loadedRecent = false;
      this.recentCount = Math.min(getRecentIds().length, RECENT_LIMIT);

      this.onClick = this.onClick.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);
      root.addEventListener('click', this.onClick);
      document.addEventListener('cart:updated', this.onCartUpdated);

      this.applyTab();
      this.watchOpenState();
      this.openFromUrl();
      this.warmImages();
      this.watchAddToCart();
    }

    /* ---------------- Apertura ---------------- */

    // /cart redirige al inicio con ?cart=open (layout/theme.liquid): se abre el drawer y se
    // quita el parámetro para que no vuelva a abrirse al recargar o al compartir la URL.
    openFromUrl() {
      const url = new URL(window.location.href);
      if (url.searchParams.get('cart') !== 'open') return;
      url.searchParams.delete('cart');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);

      customElements.whenDefined('m-cart-drawer').then(() => {
        requestAnimationFrame(() => {
          if (this.tab !== 'cart') this.setTab('cart');
          if (typeof this.root.open === 'function') this.root.open();
        });
      });
    }

    // cart.js abre y cierra solo con --translate-x: se refleja en .is-open para usar una
    // curva de apertura distinta a la de cierre.
    watchOpenState() {
      const root = this.root;
      const sync = () => {
        root.classList.toggle('is-open', root.style.getPropertyValue('--translate-x').trim() === '0');
      };

      new MutationObserver((mutations) => {
        // Arranque inmediato: cart.js espera dos frames tras activar el drawer antes de
        // moverlo. Como el drawer ya está maquetado (ver CSS), la transición se lanza en
        // cuanto se activa; cart.js luego fija los mismos valores.
        const activated = mutations.some(
          (m) =>
            m.attributeName === 'class' &&
            root.classList.contains('m-cart-drawer--active') &&
            !(m.oldValue || '').includes('m-cart-drawer--active')
        );
        if (activated && root.style.getPropertyValue('--translate-x').trim() !== '0') {
          root.style.setProperty('--m-bg-opacity', '0.5');
          root.style.setProperty('--translate-x', '0');
        }
        sync();
      }).observe(root, { attributes: true, attributeFilter: ['style', 'class'], attributeOldValue: true });

      sync();
    }

    // Las imágenes del carrito son lazy: se descargaban y decodificaban al abrir, en plena
    // animación. Se precargan y decodifican en un momento ocioso.
    warmImages() {
      const run = () => {
        this.root.querySelectorAll('.enova-minicart-item__image img').forEach((img) => {
          if (img.loading === 'lazy') img.loading = 'eager';
          if (img.decode) img.decode().catch(() => {});
        });
      };
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2000 });
      else setTimeout(run, 600);
    }

    // El formulario de producto del tema espera la respuesta de /cart/add antes de abrir el
    // drawer (300 ms – 1 s). Aquí se abre en el mismo clic, con la línea de carga, y el
    // producto aparece cuando el tema vuelve a pintar el carrito.
    watchAddToCart() {
      const settings = window.MinimogSettings || {};
      if (!settings.use_ajax_atc || !settings.enable_cart_drawer) return;

      const finish = () => {
        clearTimeout(this.updatingTimer);
        this.root.classList.remove('is-updating');
      };

      // Fase de burbuja en document: el handler del tema (en el form) ya corrió. Si validó y
      // está enviando, <product-form> tiene .m-spinner-loading.
      document.addEventListener('submit', (event) => {
        const productForm = event.target.closest && event.target.closest('product-form');
        if (!productForm || !productForm.classList.contains('m-spinner-loading')) return;
        if (typeof this.root.open !== 'function') return;

        if (this.tab !== 'cart') this.setTab('cart');
        this.root.classList.add('is-updating');
        clearTimeout(this.updatingTimer);
        this.updatingTimer = setTimeout(finish, 8000);
        this.root.open(event.submitter || null);
      });

      document.addEventListener('product-ajax:added', finish);
      document.addEventListener('product-ajax:error', () => {
        if (!this.root.classList.contains('is-updating')) return;
        finish();
        if (typeof this.root.close === 'function') this.root.close();
      });
    }

    onCartUpdated() {
      this.applyTab();
      this.warmImages();
      if (this.loadedRecent) this.scheduleRecentSync();
    }

    /* ---------------- Pestañas ---------------- */

    get tab() {
      return this.root.dataset.tab || 'cart';
    }

    setTab(tab) {
      this.root.dataset.tab = tab;
      this.applyTab();
      if (tab === 'recent') this.loadRecent();
    }

    // Se vuelve a aplicar tras cada cambio del carrito: cart.js re-renderiza el encabezado.
    applyTab() {
      const tab = this.tab;
      this.root.querySelectorAll('[data-enova-minicart-tab]').forEach((button) => {
        const active = button.dataset.enovaMinicartTab === tab;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
      });

      const cartPanel = this.root.querySelector('#MinimogCartDrawerBody');
      const footer = this.root.querySelector('#MinimogCartDrawerFooter');
      if (cartPanel) cartPanel.hidden = tab !== 'cart';
      if (footer) footer.hidden = tab !== 'cart';
      if (this.recent) this.recent.hidden = tab !== 'recent';

      this.renderRecentCount();
    }

    renderRecentCount() {
      const counter = this.root.querySelector('[data-enova-recent-count]');
      if (counter) counter.textContent = String(this.recentCount);
    }

    setRecentCount(count) {
      this.recentCount = count;
      this.renderRecentCount();
    }

    onClick(event) {
      const tabButton = event.target.closest('[data-enova-minicart-tab]');
      if (tabButton) {
        event.preventDefault();
        this.setTab(tabButton.dataset.enovaMinicartTab);
        return;
      }

      const toggle = event.target.closest('[data-enova-recent-toggle]');
      if (toggle) {
        event.preventDefault();
        this.handleToggle(toggle);
        return;
      }

      const addSize = event.target.closest('[data-enova-recent-add]');
      if (addSize) {
        event.preventDefault();
        this.handleAdd(addSize);
      }
    }

    /* ---------------- Vistos recientemente ---------------- */

    renderRecentMessage(message) {
      this.recent.innerHTML = `<p class="enova-minicart__empty">${message}</p>`;
    }

    loadRecent() {
      if (!this.recent || this.loadedRecent) return;

      const ids = getRecentIds();
      if (!ids.length) {
        this.renderRecentMessage(this.msg.msgEmpty);
        this.loadedRecent = true;
        this.setRecentCount(0);
        return;
      }

      const query = ids
        .slice(0, RECENT_LIMIT)
        .map((id) => `id:${id}`)
        .join(' OR ');
      const url = `${this.recent.dataset.searchUrl || '/search'}?section_id=enova-minicart-recent&type=product&q=${encodeURIComponent(query)}`;

      this.renderRecentMessage(this.msg.msgLoading);

      fetch(url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const list = html.querySelector('.enova-minicart-recent-list');
          if (list) this.recent.innerHTML = list.innerHTML;
          else this.renderRecentMessage(this.msg.msgEmpty);
          this.loadedRecent = true;
          this.setRecentCount(this.recent.querySelectorAll('[data-enova-recent-item]').length);
          this.syncRecentCart();
        })
        .catch(() => this.renderRecentMessage(this.msg.msgError));
    }

    // Producto sin selector de talla: agrega directo. Con selector: abre o cierra las tallas.
    handleToggle(button) {
      const item = button.closest('[data-enova-recent-item]');
      if (!item || button.disabled) return;

      if (item.dataset.hasPicker !== 'true') {
        this.handleAdd(button, item.dataset.variantId);
        return;
      }

      const expanded = item.classList.contains('is-open');
      this.recent.querySelectorAll('[data-enova-recent-item].is-open').forEach((openItem) => {
        if (openItem !== item) this.setExpanded(openItem, false);
      });
      this.setExpanded(item, !expanded);
    }

    setExpanded(item, open) {
      item.classList.toggle('is-open', open);
      const toggle = item.querySelector('[data-enova-recent-toggle]');
      const sizes = item.querySelector('[data-enova-recent-sizes]');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
      if (sizes) sizes.hidden = !open;
    }

    handleAdd(button, variantId) {
      const id = variantId || button.dataset.variantId;
      if (!id || button.disabled || button.classList.contains('is-loading')) return;

      button.classList.add('is-loading');
      button.disabled = true;

      addToCart(id)
        .then((response) => {
          emitCartUpdate(response);
          return this.syncRecentCart().then(() => this.flashAdded(button));
        })
        .catch((error) => showError((error && error.message) || this.msg.msgAddError))
        .finally(() => {
          button.classList.remove('is-loading');
          button.disabled = false;
        });
    }

    scheduleRecentSync() {
      clearTimeout(this.recentSyncTimer);
      this.recentSyncTimer = setTimeout(() => this.syncRecentCart(), 120);
    }

    // Marca cuántas unidades de cada producto y talla ya están en el carrito.
    syncRecentCart() {
      if (!this.recent) return Promise.resolve();
      const cartUrl = (getRoutes().cart_url || '/cart').replace(/\/$/, '');

      return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const byVariant = {};
          const byProduct = {};
          (cart.items || []).forEach((line) => {
            byVariant[line.variant_id] = (byVariant[line.variant_id] || 0) + line.quantity;
            byProduct[line.product_id] = (byProduct[line.product_id] || 0) + line.quantity;
          });

          this.recent.querySelectorAll('[data-enova-recent-item]').forEach((item) => {
            const qty = byProduct[item.dataset.productId] || 0;
            const incart = item.querySelector('[data-enova-recent-incart]');
            const incartQty = item.querySelector('[data-enova-recent-incart-qty]');
            item.classList.toggle('is-in-cart', qty > 0);
            if (incart) incart.hidden = qty === 0;
            if (incartQty) incartQty.textContent = qty;

            item.querySelectorAll('[data-enova-recent-add]').forEach((button) => {
              const sizeQty = byVariant[button.dataset.variantId] || 0;
              const badge = button.querySelector('[data-enova-recent-size-qty]');
              button.classList.toggle('is-in-cart', sizeQty > 0);
              if (badge) badge.textContent = sizeQty > 0 ? sizeQty : '';
            });
          });
        })
        .catch(() => {});
    }

    flashAdded(button) {
      const item = button.closest('[data-enova-recent-item]');
      [button, item].forEach((node) => {
        if (!node) return;
        node.classList.remove('is-just-added');
        void node.offsetWidth; // reinicia la animación si se agrega varias veces seguidas
        node.classList.add('is-just-added');
      });
      clearTimeout(button.enovaAddedTimer);
      button.enovaAddedTimer = setTimeout(() => {
        button.classList.remove('is-just-added');
        if (item) item.classList.remove('is-just-added');
      }, 1400);
    }
  }

  function init() {
    document.querySelectorAll('.enova-minicart').forEach((root) => {
      if (root.dataset.enovaMinicartReady === 'true') return;
      root.dataset.enovaMinicartReady = 'true';
      new EnovaMinicart(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
